<template>
  <div class="grid-container grid-rows-[min-content_auto] md:mt-6 md:mb-12">
    <aside class="col-span-full md:col-start-2 md:col-end-6 xl:col-span-4">
      <div class="md:p-4 md:elevation md:rounded-lg md:border md:border-outline/16">
        <!-- ProfileDetailsSection -->
        <div class="relative md:p-1">
          <ProfileBanner class="h-24 md:hidden" :src="profile.company.banner_image" />
          <ProfilePicture
            with-border
            :src="profile.company.profile_image"
            :size="$window.width <= 905 ? 'medium' : 'large'"
            class="absolute right-1/2 top-1/2 translate-x-1/2 hover:shadow-none md:static md:flex md:justify-center md:transform-none"
          />
        </div>
        <div class="mt-10 md:m-0">
          <div class="md:flex md:flex-col md:gap-2 md:p-2">
            <!-- Verified Badge -->
            <div class="flex gap-2 justify-center items-center">
              <Icon name="outline-verified" class="fill-brand-bright-green" />
              <span class="text-chips text-tertiary">Empresa destacada</span>
            </div>
            <h1 class="headline-sm text-center md:headline-md">
              {{ profile.company.name }}
            </h1>
            <h2 class="title-md text-center md:hidden">
              {{ profile.title }}
            </h2>
          </div>
          <div class="flex gap-4 p-2 justify-center fill-primary md:justify-evenly md:p-2">
            <!-- Social icons -->
            <a
              v-for="(item, key) in profileSocialMedia"
              :key="key"
              :href="item.found_value"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ButtonIcon :icon="item.icon" class="btn-text" />
            </a>
          </div>
          <div
            class="flex gap-[2px] overflow-x-scroll p-1 sm:justify-center md:flex-col md:overflow-auto md:p-0"
          >
            <ListItemWithDivider
              v-if="profile.company_visibility"
              :with-divider="$window.width >= 905"
            >
              <a
                :href="$route('company.stand', profile.company.id)"
                class="btn-text block fill-primary min-w-fit md:w-full"
                >Ir al perfil de la empresa</a
              >
            </ListItemWithDivider>
            <ListItemWithDivider :with-divider="$window.width >= 905">
              <Button
                class="btn-text block fill-primary min-w-fit md:w-full"
                icon="round-cases"
                @click="scrollTo('relatedOffers')"
                >Ver ofertas relacionadas</Button
              >
            </ListItemWithDivider>
            <ListItemWithDivider :with-divider="$window.width >= 905">
              <!-- TODO: Implementacion del metodo shareProfile -->
              <Button
                class="btn-text fill-primary min-w-fit md:w-full"
                icon="share"
                @click="shareProfile"
                >Compartir</Button
              >
            </ListItemWithDivider>
          </div>
          <Divider class="mx-0 md:hidden" />
        </div>
      </div>
    </aside>
    <main class="md:col-start-6 md:col-end-14 xl:col-span-8">
      <div class="flex flex-col gap-2 md:gap-0 md:rounded-lg md:border md:border-outline/16">
        <section class="flex flex-col gap-2 md:p-7 md:pt-4">
          <!-- Title container -->
          <div class="py-4 md:py-0">
            <div class="flex flex-wrap gap-3 items-center justify-between">
              <h2 class="headline-sm text-start">
                {{ profile.title }}
              </h2>
              <Button
                v-if="!isCompanyUser"
                class="btn-filled brand-green fill-white"
                :disabled="profile.has_user_applied"
                :isLoading="applyButtonIsLoading"
                @click="submitApplication"
              >
                {{ profile.has_user_applied ? 'Ya has aplicado' : '¡Aplica ahora!' }}
              </Button>
              <!-- Go to Admin route -->
              <Link v-if="isUserOwner" :href="$route('company.jobs.edit', profile.id)">
                <Button class="btn-filled brand-green fill-white" icon="settings-filled">
                  Modificar
                </Button>
              </Link>
            </div>
            <Divider class="hidden md:block md:!w-auto md:!mx-0" />
          </div>
          <div class="flex flex-col gap-2 md:gap-6 md:py-4">
            <!-- Personal info chips -->
            <div class="flex flex-col md:flex-row md:flex-wrap md:gap-2">
              <div
                v-for="(bullet, key) of profileInfoBullets"
                :key="key"
                class="flex gap-2 items-center md:flex-col md:gap-0 md:items-start"
              >
                <p class="text-chips px-2">{{ bullet.name }}</p>
                <p class="body-lg p-2">
                  {{ bullet.found_value }}
                </p>
              </div>
            </div>
            <ProfileSection class="pb-2">
              <template #title>Descripción de la oferta</template>
              <template #content>
                <p v-if="profile.description.full" class="body-lg pt-2">
                  {{ profile.description.full }}
                </p>
              </template>
            </ProfileSection>
            <ProfileSection
              v-if="profile.job_responsibilities && profile.job_responsibilities.length > 0"
            >
              <template #title>Funciones</template>
              <template #content>{{ profile.job_responsibilities }}</template>
            </ProfileSection>
            <ProfileSection
              v-if="profile.required_qualifications && profile.required_qualifications.length > 0"
            >
              <template #title>Requisitos</template>
              <template #content>{{ profile.required_qualifications }}</template>
            </ProfileSection>
            <ProfileSection
              v-if="profile.benefits_and_perks && profile.benefits_and_perks.length > 0"
            >
              <template #title>Prestaciones</template>
              <template #content>{{ profile.benefits_and_perks }}</template>
            </ProfileSection>
            <ProfileSection id="relatedOffers" sections="true">
              <template #title>Ofertas de empleo relacionadas</template>
              <template #content>
                <div v-if="relatedOffers && relatedOffers.length > 0">
                  <div class="flex flex-col gap-2">
                    <ListItemWithDivider v-for="(offer, key) in relatedOffers" :key="offer.id">
                      <Link :href="$route('jobposting', offer.id)" class="w-full">
                        <OfferCard :offer="{ ...offer }" surface show-chips />
                      </Link>
                    </ListItemWithDivider> 
                  </div>
                  <div class="w-full my-4">
                    <Link :href="$route('search')">
                      <Button class="btn-outlined block mx-auto">Explora más ofertas</Button>
                    </Link>
                  </div>
                </div>
                <div v-else class="flex flex-col gap-2 justify-center items-center pb-4">
                  <Illustration name="lost" class="rounded-2xl w-full max-h-36 p-2" />
                  <p class="label-lg text-center text-on-surface-variant">
                    Parece que la empresa no ha publicado otra oferta...
                  </p>
                  <Link :href="$route('search')">
                    <Button class="btn-outlined">Explora más ofertas</Button>
                  </Link>
                </div>
              </template>
            </ProfileSection>
          </div>
          <modal
            name="upload_cv_modal"
            height="auto"
            :reset="true"
            :adaptive="true"
            :scrollable="true"
            classes="!surface-1 md:!rounded-xl !overflow-y-auto"
          >
            <div class="py-6 px-4 md:p-6 surface1">
              <div class="flex justify-between items-center mb-4">
                <h4 class="headline-5 py-2">¡Agrega tu CV!</h4>
                <ButtonIcon
                  icon="close"
                  class="btn-text-link"
                  @click="$modal.hide('upload_cv_modal')"
                />
              </div>
              <form class="p-4 text-center mb-2" @submit.prevent="uploadCVFile">
                <Illustration name="add-files" class="w-full max-h-36 p-2" />
                <p class="title-lg text-center text-primary mb-1">¡Agrega tu curriculum vitae!</p>
                <p class="title-sm text-neutral40 mb-4">
                  Puedes agregar un archivo en formato PDF. El tamaño máximo admitido es de 5 MB.
                </p>
                <FormInputFile
                  accept=".pdf"
                  show-file-size
                  :max-file-size="2242880"
                  @update-files="upload_cv_form.cv_file = $event[0]"
                ></FormInputFile>
                <div class="mt-3 text-center">
                  <Button type="submit" class="btn-filled" :isLoading="upload_cv_form.processing">
                    ¡Subir archivo y aplicar!
                  </Button>
                  <Button type="button" class="btn-tonal" @click="$modal.hide('upload_cv_modal')">
                    Cancelar
                  </Button>
                </div>
              </form>
            </div>
          </modal>
        </section>
      </div>
    </main>
  </div>
</template>

<script>
import getPropertiesIfExistFromDataObjectMixin from '@/getPropertiesIfExistFromDataObject.mixin.js'
import evalUserProfileTypeMixin from '@/evalUserProfileType.mixin.js'
import { Link, Head } from '@inertiajs/inertia-vue'
import Layout from '@/Layouts/Layout'
import Icon from '@/Components/Icon'
import Button from '@/Components/Button'
import ButtonIcon from '@/Components/ButtonIcon'
import Divider from '@/Components/Divider'
import ProfileBanner from '@/Components/ProfileBanner'
import ProfilePicture from '@/Components/ProfilePicture'
import ProfileSection from '@/Components/ProfileSection'
import ListItemWithDivider from '@/Components/ListItemWithDivider'
import ChipListItem from '@/Components/ChipListItem'
import ProfileListItem from '@/Components/ProfileListItem'

export default {
  name: 'JobPostingProfile',
  layout: Layout,
  components: {
    Link,
    Head,
    Icon,
    Button,
    ButtonIcon,
    Divider,
    ProfileBanner,
    ProfilePicture,
    ProfileSection,
    ListItemWithDivider,
    ChipListItem,
    ProfileListItem,
    Illustration: () => import('@/Components/Illustration'),
    FormInputFile: () => import('@/Components/FormInputFile'),
    OfferCard: () => import('@/Components/OfferCard'),
  },
  mixins: [getPropertiesIfExistFromDataObjectMixin, evalUserProfileTypeMixin],
  props: {
    isUserOwner: { type: Boolean, required: false, default: false },
    profile: { type: Object, required: true },
    relatedOffers: { type: Array, required: false },
    errors: { type: Object, required: false },
    fileDownloadToken: { type: Object, required: false },
  },
  data() {
    return {
      applyButtonIsLoading: false,
      upload_cv_form: this.$inertia.form({
        cv_file: null,
      }),
    }
  },
  computed: {
    profileInfoBullets() {
      return this.getPropertiesIfExistFromDataObject('profile', {
        salary: { name: 'Salario' },
        'category.name': { name: 'Categoría' },
        'required_education.name': { name: 'Escolaridad requerida' },
        job_contract_type: { name: 'Tipo de Contrato' },
      }).map(item => {
        if (item.name == 'Salario') {
          const label = item.found_value.to_be_agreed
            ? 'A convenir'
            : item.found_value.max
            ? `$${item.found_value.min} - $${item.found_value.max}`
            : `$${item.found_value.min}`
          return { ...item, found_value: label }
        } else return item
      })
    },
    profileSocialMedia() {
      return this.getPropertiesIfExistFromDataObject('profile', {
        'company.whatsapp_url': { icon: 'whatsapp' },
        'company.fb_url': { icon: 'round-facebook' },
        'company.linkedin_url': { icon: 'linkedin' },
        'company.website_url': { icon: 'web' },
      })
    },
  },
  methods: {
    scrollTo(element) {
      const $element = document.getElementById(element)
      if ($element === null) console.warning(`${element} is not registered.`)
      else $element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    },
    uploadCVFile(event) {
      this.upload_cv_form.post(this.$route('cv.store'), {
        onSuccess: () => {
          this.$modal.hide('upload_cv_modal')
          this.submitApplication()
        },
        onError: errors => {
          this.$swal.fire({
            icon: 'error',
            title: 'Error.',
            html: `
                <ul class="text-start body-lg">
                  ${Object.values(errors)
                    .map(item => `<li>${item}</li>`)
                    .join('')}
                </ul>
              `,
          })
        },
      })
    },
    submitApplication() {
      if (this.isGuestUser) return this.$inertia.get(this.$route('register'))
      this.$inertia.post(
        this.$route('application.store'),
        { job_posting_id: this.profile.id },
        {
          onBefore: () => (this.applyButtonIsLoading = true),
          onFinish: () => (this.applyButtonIsLoading = false),
          onSuccess: page => {
            if (Object.keys(this.errors).length <= 0)
              this.$swal.fire({
                title: '¡Guardado con éxito!',
                text: page.props.flash.message
                  ? page.props.flash.message
                  : '🎉 Has aplicado con éxito a la plaza',
                icon: 'success',
              })
          },
          onError: errors => {
            this.$swal
              .fire({
                icon: 'error',
                title: 'Error. No se pudo guardar tu aplicación.',
                html: `
                <ul class="text-start body-lg">
                  ${Object.values(errors)
                    .map(item => `<li>${item}</li>`)
                    .join('')}
                </ul>
              `,
              })
              .then(result => {
                if (errors.hasOwnProperty('cv_not_found')) {
                  this.$modal.show('upload_cv_modal')
                }
              })
          },
        },
      )
    },
    shareProfile() {
      console.log('share')
      //   const shareData = {
      //       url: window.location,
      //       //   title: `Perfil de ${this.company.name} | Bolsa de Trabajo`,
      //     },
      //     sweetAlertConfig = {
      //       toast: true,
      //       timer: 3000,
      //       position: 'bottom',
      //       showConfirmButton: false,
      //       timerProgressBar: true,
      //     }

      //   this.$swal.fire({ ...sweetAlertConfig, title: 'Copiado al portapapeles' })
    },
  },
}
</script>

<style></style>
